import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes } from 'react-icons/fa';
import CustomModal from '../CustomModal/CustomModal';
import api from '../../services/api';
import { useAuth } from '../../contexts/auth';
import CustomButton from '../CustomButton/CustomButton';
import { EstimateViewModel } from '../../models/estimate/EstimateViewModel';

interface EstimateEmailToProps {
  estimate: EstimateViewModel;
  isOpen: boolean;
  setIsOpenModal: (isOpen: boolean) => void;
  refreshEstimate: () => void;
}

interface EstimateEmailToFields {
  to: string;
}

export default function EstimateEmailTo({
  estimate,
  isOpen,
  setIsOpenModal,
  refreshEstimate,
}: EstimateEmailToProps) {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<EstimateEmailToFields>();

  const [formErrors, setFormErrors] = useState<Record<string, any>>([]);
  const [to, setTo] = useState<string[]>([]);

  const {
    user: { organization },
  } = useAuth();

  const addTo = () => {
    const value = getValues('to');
    const arr = [...to];
    if (value && to.indexOf(value) === -1) arr.push(value);
    setTo(arr);
    setValue('to', '');
  };

  const removeTo = (value: string) => {
    const arr = [...to];
    const index = arr.indexOf(value);
    if (index !== -1) arr.splice(index, 1);
    setTo(arr);
  };

  const onSubmit = async (data: EstimateEmailToFields) => {
    try {
      if (to.length === 0 && !data.to) {
        toast.info('Adicione pelo menos um destinatário');
        return;
      }
      const arr = [...to];
      if (data.to) arr.push(data.to);
      const payload = arr.join(',');
      await api.post(
        `organizations/${organization.id}/estimates/${estimate.id}/email`,
        {
          to: payload,
        }
      );

      toast.success('O Orçamento foi enviado');
      setIsOpenModal(false);
      setTo([]);
      refreshEstimate();
    } catch (e: any) {
      if (e?.response?.data?.errors) {
        if (e?.response?.status === 422) {
          setFormErrors(e.response.data.errors);
        } else {
          toast.error(
            e?.response?.data?.errors.reduce(
              (acc: string, error: string) => acc + error,
              ''
            )
          );
        }
      } else {
        throw e;
      }
    }
  };

  useEffect(() => {
    if (estimate && estimate.customerEmail)
      setValue('to', estimate.customerEmail);
  }, [estimate]);

  return (
    <CustomModal
      modalTitle='Informe os destinatários'
      isOpen={isOpen}
      setIsOpenModal={setIsOpenModal}
    >
      <form>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label htmlFor='inputLabel' className='form-label'>
          Destinatário
        </label>
        <div className='input-group mb-3'>
          <input
            {...register('to')}
            type='email'
            className='form-control'
            placeholder='Destinatário'
            aria-label='Destinatário'
            aria-describedby='button-addon2'
          />
          <button
            className='btn btn-outline-secondary'
            type='button'
            id='button-addon2'
            onClick={addTo}
          >
            <FaPlus />
          </button>
        </div>
        {errors.to && (
          <div className='form-text text-danger'>{errors.to.message}</div>
        )}
        <div
          className='d-flex flex-direction-row flex-wrap gap-1'
          style={{ maxWidth: '20rem' }}
        >
          {to.map((t) => (
            <div
              className='btn btn-outline-secondary btn-sm d-flex align-items-center gap-1'
              key={t}
            >
              <span>{t}</span>
              <FaTimes onClick={() => removeTo(t)} />
            </div>
          ))}
        </div>
        <div className='text-end mt-3'>
          <CustomButton
            type='submit'
            className='btn btn-primary'
            handleClick={handleSubmit(onSubmit)}
          >
            Enviar
          </CustomButton>
        </div>
      </form>
    </CustomModal>
  );
}
